import { createContext, useContext, useEffect, useState, useCallback } from 'react';
import { api, tokenStore } from '../api/client.js';

const AuthContext = createContext(null);

export function AuthProvider({ children }) {
    const [user, setUser] = useState(null);
    const [loading, setLoading] = useState(true);

    // On mount: if we have a token, validate it by fetching /me
    useEffect(() => {
        const token = tokenStore.get();
        if (!token) {
            setLoading(false);
            return;
        }
        api.get('/auth/me')
            .then(setUser)
            .catch(() => { tokenStore.clear(); setUser(null); })
            .finally(() => setLoading(false));
    }, []);

    // Listen for the global "auth expired" event fired by the API client
    useEffect(() => {
        const handler = () => setUser(null);
        window.addEventListener('behold:auth-expired', handler);
        return () => window.removeEventListener('behold:auth-expired', handler);
    }, []);

    const login = useCallback(async (email, password) => {
        const result = await api.post('/auth/login', { email, password });
        tokenStore.set(result.token);
        setUser(result.user);
        return result.user;
    }, []);

    const logout = useCallback(() => {
        tokenStore.clear();
        setUser(null);
    }, []);

    return (
        <AuthContext.Provider value={{ user, loading, login, logout }}>
            {children}
        </AuthContext.Provider>
    );
}

export function useAuth() {
    const ctx = useContext(AuthContext);
    if (!ctx) throw new Error('useAuth must be used inside <AuthProvider>');
    return ctx;
}
